import type { CampaignDraftCreatorItem } from '@/entities/campaign';
import { useCallback, useEffect, useState } from 'react';

import {
  type CampaignCreateIssue,
  getCreatorRowError,
} from '../../../../hooks/campaign-create-shared';

type UseCampaignCreatorValidationArgs = {
  creatorItems: CampaignDraftCreatorItem[];
  draftCreatorItems: CampaignDraftCreatorItem[];
};

export type CampaignCreatorValidation = ReturnType<typeof useCampaignCreatorValidation>;

export function useCampaignCreatorValidation({
  creatorItems,
  draftCreatorItems,
}: UseCampaignCreatorValidationArgs) {
  const [creatorInvalidRowIds, setCreatorInvalidRowIds] = useState<string[]>([]);
  const [creatorIssues, setCreatorIssues] = useState<CampaignCreateIssue[]>([]);

  useEffect(() => {
    const existingRowIds = new Set(creatorItems.map((item) => item.id));

    setCreatorInvalidRowIds((currentRowIds) => {
      const nextRowIds = currentRowIds.filter((rowId) => existingRowIds.has(rowId));
      return nextRowIds.length === currentRowIds.length ? currentRowIds : nextRowIds;
    });

    setCreatorIssues((currentIssues) => {
      const nextIssues = currentIssues.filter((issue) => existingRowIds.has(issue.rowId));
      return nextIssues.length === currentIssues.length ? currentIssues : nextIssues;
    });
  }, [creatorItems]);

  const clearCreatorValidation = useCallback((rowId: string) => {
    setCreatorInvalidRowIds((currentRowIds) =>
      currentRowIds.filter((currentRowId) => currentRowId !== rowId),
    );
    setCreatorIssues((currentIssues) =>
      currentIssues.filter((currentIssue) => currentIssue.rowId !== rowId),
    );
  }, []);

  const resetCreatorValidation = useCallback(() => {
    setCreatorInvalidRowIds([]);
    setCreatorIssues([]);
  }, []);

  const setCreatorValidationIssue = useCallback((issue: CampaignCreateIssue) => {
    setCreatorInvalidRowIds((currentRowIds) =>
      currentRowIds.includes(issue.rowId) ? currentRowIds : [...currentRowIds, issue.rowId],
    );
    setCreatorIssues((currentIssues) => {
      const nextIssues = currentIssues.filter((currentIssue) => currentIssue.rowId !== issue.rowId);
      return [...nextIssues, issue];
    });
  }, []);

  const validateCreatorRows = useCallback(() => {
    const nextIssues = draftCreatorItems
      .map((item) => ({
        rowId: item.id,
        message: getCreatorRowError(item),
      }))
      .filter((item): item is CampaignCreateIssue => Boolean(item.message));

    setCreatorIssues(nextIssues);
    setCreatorInvalidRowIds(nextIssues.map((issue) => issue.rowId));

    return nextIssues;
  }, [draftCreatorItems]);

  return {
    clearCreatorValidation,
    creatorInvalidRowIds,
    creatorIssues,
    isReadyToLeaveStage: creatorIssues.length === 0,
    resetCreatorValidation,
    setCreatorValidationIssue,
    validateCreatorRows,
  };
}
